import { Button, Label, RadioGroup, ScrollView, SizableText, Spinner, XStack, YStack } from '@my/ui'
import { Group } from 'app/types'
import { GroupAvatar } from '../../components/group-avatar'
import { PostCreationHeader } from './post-creation-header'

type RadioGroupItemProps = {
  value: string
  group: Pick<Group, 'id' | 'name' | 'visibility' | 'totalMembers' | 'profileImage'>
}

const RadioGroupItem = ({ value, group }: RadioGroupItemProps) => {
  const id = `radiogroup-${value}`
  return (
    <XStack
      flex={1}
      jc="space-between"
      ai="center"
      gap="$2"
      $gtSm={{
        px: '$3.5',
      }}
    >
      <XStack fd="row" flexGrow={1} flexShrink={1} maxHeight={60} ai="center">
        <GroupAvatar src={group.profileImage?.url} width={44} height={44} />
        <YStack flexShrink={1} pl="$2">
          <Label
            unstyled
            htmlFor={id}
            numberOfLines={1}
            fontSize="$4"
            fow="400"
            height="auto"
            cursor="pointer"
          >
            {group.name}
          </Label>
          <SizableText size="$1">
            {group.visibility === 'public' ? 'Public' : 'Private'} | {group.totalMembers}{' '}
            {group.totalMembers === 1 ? 'Member' : 'Members'}
          </SizableText>
        </YStack>
      </XStack>

      <RadioGroup.Item flexShrink={0} value={value} id={id} size="$4" cursor="pointer">
        <RadioGroup.Indicator />
      </RadioGroup.Item>
    </XStack>
  )
}

type PostCreationGroupSelectProps = {
  groups: Pick<Group, 'id' | 'name' | 'visibility' | 'totalMembers' | 'profileImage'>[]
  groupsLoading?: boolean
  onGroupChange: (group: Pick<Group, 'id' | 'name'>) => void
  activeGroupId?: string
  onOpenChange: (open: boolean) => void
  onBack: () => void
}

export const PostCreationGroupSelect = ({
  onBack,
  groups,
  activeGroupId,
  groupsLoading,
  onGroupChange,
}: PostCreationGroupSelectProps) => {
  const handleValueChange = (value: string) => {
    const group = groups.find((group) => group.id === value)
    if (group) {
      onGroupChange({
        id: group.id,
        name: group.name,
      })
    }
  }

  return (
    <YStack flex={1} gap="$3.5">
      {/* Header */}
      <PostCreationHeader title="Select Group" onBack={onBack}>
        <Button
          size="$3"
          $gtSm={{
            size: '$4',
          }}
          onPress={onBack}
          theme="primary"
        >
          Done
        </Button>
      </PostCreationHeader>
      <YStack gap="$3.5" flex={1}>
        <YStack>
          <SizableText size="$4" fow="500">
            Where do you want to post this?
          </SizableText>
          <SizableText color="$overlay11" size="$2" $gtSm={{ size: '$3' }}>
            You can only choose group at a time when posting content.
          </SizableText>
        </YStack>

        {groupsLoading && (
          <YStack jc="center" ai="center" flex={1}>
            <Spinner />
          </YStack>
        )}

        {!groupsLoading && groups.length === 0 && (
          <YStack jc="center" ai="center" flex={1}>
            <SizableText size="$3">You haven't joined any groups yet.</SizableText>
          </YStack>
        )}

        {!groupsLoading && groups.length > 0 && (
          <ScrollView showsVerticalScrollIndicator={false}>
            <RadioGroup
              onValueChange={handleValueChange}
              name="group-select"
              {...(activeGroupId && { defaultValue: activeGroupId })}
            >
              <YStack jc="center" gap="$3.5">
                {groups.map((group) => (
                  <RadioGroupItem key={group.id} value={group.id} group={group} />
                ))}
              </YStack>
            </RadioGroup>
          </ScrollView>
        )}
      </YStack>
    </YStack>
  )
}
